/**
 * Assessment Blueprint Generator
 *
 * Creates deterministic assessment blueprints with smart question selection.
 *
 * ╔══════════════════════════════════════════════════════════════════╗
 * ║  CRITICAL INVARIANT: ASSESSMENT = MULTIPLE CHOICE ONLY         ║
 * ║                                                                ║
 * ║  This generator ONLY selects multiple_choice questions.        ║
 * ║  This is enforced at THREE levels:                             ║
 * ║  1. Policy engine (defaultPolicy.ts) — MC-only eligible        ║
 * ║  2. Eligibility predicate (assessmentEligibility.ts) — MC guard║
 * ║  3. This generator — isMultipleChoice() filter                 ║
 * ║                                                                ║
 * ║  DO NOT remove the MC filter. It is intentional.               ║
 * ╚══════════════════════════════════════════════════════════════════╝
 *
 * DDD co-locatie: moved from components/assessment/utils/ in s59.
 */

import {
  AssessmentBlueprint,
  AssessmentConfig,
  BlueprintQuestion,
  SelectionPlan,
} from '../types'
import {
  type ModuleId,
  type Question,
  getFamiliesForModule,
  type FamilyDefinition,
  QUESTIONS_BY_FAMILY,
  getAllowedTiersForGroup,
  MODULES,
} from '../../content'
import type { FamilyMasteryState } from '../../mastery'
import { getFamilyMasteryKey } from '../../mastery'
import { GroupId } from '../../config/systemConfig'
import { createRng, SeededRng } from '../../utils/deterministicRng'
import { isAssessmentEligible } from './assessmentEligibility'
import { debug } from '../../utils/logger'
import {
  isBegrijpendLezenFamily,
  isTaalverzorgingTaaFamily,
} from './assessmentDomainConfig'

const DEV = process.env.NODE_ENV === 'development'
function log(...args: unknown[]) {
  if (DEV) debug('[AssessmentBlueprint]', ...args)
}

// ── Blueprint Configuration Constants ────────────────────────────────────
/** Mastery score below which a family is categorized as 'weak' */
export const MASTERY_WEAK_THRESHOLD = 55
/** Mastery score at or above which a family is categorized as 'strong' */
export const MASTERY_STRONG_THRESHOLD = 85
/** Target percentage of questions from weak/unseen families */
export const DISTRIBUTION_WEAK_PCT = 0.6
/** Target percentage of questions from medium families */
export const DISTRIBUTION_MEDIUM_PCT = 0.3
/** Maximum questions selected per family in a single assessment */
export const MAX_QUESTIONS_PER_FAMILY = 2

export const DEFAULT_ASSESSMENT_CONFIG: AssessmentConfig = {
  questionCount: 30,
  moduleFilter: null,
  minModules: 3,
  countsForMastery: false,
  seed: undefined,
}

function categorizeMastery(
  mastery: number,
): 'weak' | 'medium' | 'strong' | 'unseen' {
  if (mastery === 0) return 'unseen'
  if (mastery < MASTERY_WEAK_THRESHOLD) return 'weak'
  if (mastery < MASTERY_STRONG_THRESHOLD) return 'medium'
  return 'strong'
}

function getFamilyMasteryScore(
  familyId: string,
  group: GroupId,
  familyMastery: Record<string, FamilyMasteryState>,
): number {
  const key = getFamilyMasteryKey(familyId, group)
  return familyMastery[key]?.masteryScore || 0
}

function isMultipleChoice(questionType?: string): boolean {
  return !questionType || questionType === 'multiple_choice'
}

function getEligibleFamilies(
  config: AssessmentConfig,
  group: GroupId,
  familyMastery: Record<string, FamilyMasteryState>,
): {
  family: FamilyDefinition
  mastery: number
  category: 'weak' | 'medium' | 'strong' | 'unseen'
  questionCount: number
}[] {
  const allowedTiers = getAllowedTiersForGroup(group)
  const modulesToInclude = config.moduleFilter || MODULES.map((m) => m.id)
  const eligibleFamilies: {
    family: FamilyDefinition
    mastery: number
    category: 'weak' | 'medium' | 'strong' | 'unseen'
    questionCount: number
  }[] = []
  let totalCustomQuestionsSkipped = 0

  modulesToInclude.forEach((moduleId) => {
    const families = getFamiliesForModule(moduleId)
    families.forEach((family) => {
      if (!allowedTiers.has(family.tier)) return

      // ── V2: Family-level TAA filtering ────────────────────────────
      // Both Taalverzorging and Begrijpend Lezen include the 'Taalbegrip' module,
      // but they need different TAA families. When domainId is set, filter accordingly.
      if (config.domainId && moduleId === 'Taalbegrip') {
        if (config.domainId === 'taalverzorging') {
          // Only include TAA-ONT-* and TAA-SOORT-* families
          if (!isTaalverzorgingTaaFamily(family.familyId)) return
        } else if (config.domainId === 'begrijpend-lezen') {
          // Only include TAA-LEZEN-*, TAA-WOORD-*, TAA-SIGNAALWOORD, TAA-ARGUMENTATIE, TAA-TEKSTSOORT
          if (!isBegrijpendLezenFamily(family.familyId)) return
        }
      }

      // ── V2: Targeted mode filtering ────────────────────────────
      if (
        config.mode === 'targeted' &&
        config.targetFamilyIds &&
        config.targetFamilyIds.length > 0
      ) {
        if (!config.targetFamilyIds.includes(family.familyId)) return
      }

      const familyQuestions = QUESTIONS_BY_FAMILY.get(family.familyId) || []
      const questionsInTier = familyQuestions.filter((q) => {
        const inTier = allowedTiers.has(q.tier)
        const isMC = isMultipleChoice(q.questionType)
        if (inTier && !isMC) totalCustomQuestionsSkipped++
        return inTier && isMC
      })
      if (questionsInTier.length === 0) return
      const mastery = getFamilyMasteryScore(
        family.familyId,
        group,
        familyMastery,
      )
      const category = categorizeMastery(mastery)
      eligibleFamilies.push({
        family,
        mastery,
        category,
        questionCount: questionsInTier.length,
      })
    })
  })

  if (totalCustomQuestionsSkipped > 0) {
    log(
      `⚠️ Skipped ${totalCustomQuestionsSkipped} custom question types (assessment requires multiple-choice only)`,
    )
  }
  return eligibleFamilies
}

function selectQuestionsFromFamily(
  familyId: string,
  count: number,
  allowedTiers: Set<number>,
  usedQuestionIds: Set<string>,
  rng: SeededRng,
): string[] {
  const familyQuestions = QUESTIONS_BY_FAMILY.get(familyId) || []
  const availableQuestions = familyQuestions.filter(
    (q) =>
      allowedTiers.has(q.tier) &&
      !usedQuestionIds.has(q.id) &&
      isAssessmentEligible(q as Question),
  )
  if (availableQuestions.length === 0) return []
  const shuffled = rng.shuffle([...availableQuestions])
  return shuffled.slice(0, Math.min(count, shuffled.length)).map((q) => q.id)
}

export function generateAssessmentBlueprint(
  config: AssessmentConfig,
  familyMastery: Record<string, FamilyMasteryState>,
  selectedGroup: GroupId,
): AssessmentBlueprint {
  const seed = config.seed || Date.now()
  const rng = createRng(seed)
  const allowedTiers = getAllowedTiersForGroup(selectedGroup)
  log(`Generating blueprint for group ${selectedGroup}, seed: ${seed}`)
  log(`🔒 Assessment mode: MC only (visuals allowed, no interactive)`)

  const eligibleFamilies = getEligibleFamilies(
    config,
    selectedGroup,
    familyMastery,
  )
  log(
    `Found ${eligibleFamilies.length} eligible families (assessment-eligible questions only)`,
  )

  const weakFamilies = eligibleFamilies.filter(
    (f) => f.category === 'weak' || f.category === 'unseen',
  )
  const mediumFamilies = eligibleFamilies.filter((f) => f.category === 'medium')
  const strongFamilies = eligibleFamilies.filter((f) => f.category === 'strong')
  log(
    `Distribution: ${weakFamilies.length} weak, ${mediumFamilies.length} medium, ${strongFamilies.length} strong`,
  )

  const targetWeak = Math.ceil(config.questionCount * DISTRIBUTION_WEAK_PCT)
  const targetMedium = Math.ceil(config.questionCount * DISTRIBUTION_MEDIUM_PCT)
  const targetStrong = config.questionCount - targetWeak - targetMedium

  const selectedQuestions: BlueprintQuestion[] = []
  const usedQuestionIds = new Set<string>()
  const usedFamilyIds = new Set<string>()

  function selectFromCategory(
    families: typeof eligibleFamilies,
    targetCount: number,
    category: 'weak' | 'medium' | 'strong',
  ) {
    const shuffledFamilies = rng.shuffle([...families])
    let selected = 0
    for (const { family, mastery } of shuffledFamilies) {
      if (selected >= targetCount) break
      if (usedFamilyIds.has(family.familyId)) continue
      const questionsToSelect = Math.min(
        MAX_QUESTIONS_PER_FAMILY,
        targetCount - selected,
      )
      const questionIds = selectQuestionsFromFamily(
        family.familyId,
        questionsToSelect,
        allowedTiers,
        usedQuestionIds,
        rng,
      )
      if (questionIds.length === 0) continue
      questionIds.forEach((qId) => {
        const question = QUESTIONS_BY_FAMILY.get(family.familyId)?.find(
          (q) => q.id === qId,
        )
        if (!question) return
        selectedQuestions.push({
          questionId: qId,
          familyId: family.familyId,
          tier: question.tier,
          module: question.module,
          masteryCategory:
            category === 'weak' && mastery === 0 ? 'unseen' : category,
        })
        usedQuestionIds.add(qId)
        selected++
      })
      usedFamilyIds.add(family.familyId)
    }
    return selected
  }

  const actualWeak = selectFromCategory(weakFamilies, targetWeak, 'weak')
  const actualMedium = selectFromCategory(
    mediumFamilies,
    targetMedium,
    'medium',
  )
  const actualStrong = selectFromCategory(
    strongFamilies,
    targetStrong,
    'strong',
  )

  const remaining = config.questionCount - selectedQuestions.length
  if (remaining > 0) {
    selectFromCategory([...eligibleFamilies], remaining, 'medium')
  }

  const shuffledQuestions = rng.shuffle(selectedQuestions)

  const selectionPlan: SelectionPlan = {
    weakPercentage: DISTRIBUTION_WEAK_PCT * 100,
    mediumPercentage: DISTRIBUTION_MEDIUM_PCT * 100,
    strongPercentage:
      (1 - DISTRIBUTION_WEAK_PCT - DISTRIBUTION_MEDIUM_PCT) * 100,
    actualDistribution: {
      weak: actualWeak,
      medium: actualMedium,
      strong: actualStrong,
    },
  }

  const blueprint: AssessmentBlueprint = {
    id: `assessment_${seed}_${selectedGroup}`,
    createdAt: Date.now(),
    selectedGroup,
    config,
    selectionPlan,
    questions: shuffledQuestions,
    masterySnapshot: {
      totalFamilies: eligibleFamilies.length,
      weakFamilies: weakFamilies.length,
      mediumFamilies: mediumFamilies.length,
      strongFamilies: strongFamilies.length,
    },
    eligibilityFilter: 'MC_ONLY_WITH_VISUALS',
    pickedQuestionIds: shuffledQuestions.map((q) => q.questionId),
  }
  log(
    `✅ Blueprint created: ${blueprint.questions.length} questions (MC with visuals allowed)`,
  )
  return blueprint
}
